import {
  Checkbox,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { isNil } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Simulator } from '~utils/types';
import { useApiClient } from '../utils/useApiClient';

interface Props {
  simulator: Simulator;
  onSuccess: () => Promise<void> | void;
}

const schema = z.object({
  max: z.number().nullable(),
});

type FormValues = z.TypeOf<typeof schema>;

export function VolumeMaxInlineEdit({ simulator, onSuccess }: Props) {
  const min = useMemo(() => simulator.shadow.state.volumeMin?.value ?? 0, [simulator]);
  const schema = useMemo(
    () =>
      z.object({
        max: z.number().nullable().optional(),
      }),
    [],
  );

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      max: simulator.shadow.state.volumeMax?.value ?? null,
    },
    resolver: zodResolver(schema),
  });

  const apiClient = useApiClient();

  const performSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await apiClient.post(`/simulators/${simulator.id}/changeVolumeMax`, {
          max: values.max,
        });

        onSuccess();
      } catch (err) {
        console.error('An error occured trying to update volume min', err);
      }
    },
    [onSuccess, simulator, apiClient],
  );

  const handleOnBlur = useCallback(() => {
    if (!isValid) return;

    handleSubmit(performSubmit)();
  }, [isValid, handleSubmit, performSubmit]);

  return (
    <form onSubmit={handleSubmit(performSubmit)}>
      <Controller
        name="max"
        control={control}
        render={({ field }) => (
          <Stack direction="row" spacing="2">
            <Checkbox
              isChecked={!isNil(field.value)}
              onChange={() => {
                field.onChange(isNil(field.value) ? 100 : null);
                handleOnBlur();
              }}
            ></Checkbox>
            <NumberInput
              value={field.value ?? undefined}
              onChange={(_, val) => field.onChange(val)}
              onBlur={handleOnBlur}
              min={min}
              max={100}
              isDisabled={isNil(field.value)}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </Stack>
        )}
      />
    </form>
  );
}
